import { useCallback, useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { Button, Icon, Layout, Page, Text } from '@loadsmart/miranda-react'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import {
  ANALYTICS_MCP_CAPABILITIES,
  ANALYTICS_MCP_SAMPLES,
  type AnalyticsMcpSample,
} from 'managed-analytics/domain/analyticsMcp'
import AskPanel from 'managed-analytics/ui/components/analyticsMcp/AskPanel'

import {
  StyledActions,
  StyledCapability,
  StyledCapabilityExample,
  StyledCapabilityGrid,
  StyledClosingAction,
  StyledClosingBand,
  StyledClosingBody,
  StyledClosingCopy,
  StyledClosingTitle,
  StyledContentColumn,
  StyledDisclaimer,
  StyledEyebrow,
  StyledHeadline,
  StyledHeadlineSoft,
  StyledLede,
  StyledSection,
} from './AnalyticsMcpPage.styles'

/**
 * The Analytics MCP preview, at /analytics-mcp. Named as a capability of the
 * reporting product rather than as an assistant or a connector: what a customer
 * gets is their analytics reachable over MCP.
 */

/**
 * Every CTA on the page is the same ask, so they share one label and one
 * dialog title — a single action should not have three names. What differs is
 * the description, which names where the visitor was when they clicked, and the
 * Mixpanel source, which is how we learn which part of the page convinces.
 */
type LeadIntent = {
  source: string
  description: string
}

const LEAD_TITLE = 'Request early access'

const HERO_LEAD: LeadIntent = {
  source: 'Hero',
  description:
    'Tell us where to reach you. We will follow up about opening this on your ShipperGuide and Opendock data.',
}

const SAMPLE_LEAD: LeadIntent = {
  source: 'Sample follow-up',
  description:
    'We will set up a working session against your data so you can ask these questions of your real numbers.',
}

const CLOSING_LEAD: LeadIntent = {
  source: 'Closing band',
  description:
    'Tell us what you want to ask and we will get back to you about setting it up on your data.',
}

export default function AnalyticsMcpPage() {
  const { track } = useAnalytics()
  const navigate = useNavigate()

  const [selectedPromptId, setSelectedPromptId] = useState(ANALYTICS_MCP_SAMPLES[0].id)
  const [leadIntent, setLeadIntent] = useState<LeadIntent | null>(null)
  const [isLeadDialogOpen, setIsLeadDialogOpen] = useState(false)

  useEffect(() => {
    const isMobile = /Mobi/i.test(window.navigator.userAgent)

    if (isMobile) {
      navigate('/mobile-not-supported')
    }
  }, [navigate])

  useEffect(() => {
    track(AnalyticsEvent.OpenAnalyticsMcp)
  }, [track])

  const openLeadForm = useCallback(
    (intent: LeadIntent) => {
      track(AnalyticsEvent.OpenAnalyticsMcpLeadForm, { Source: intent.source })
      setLeadIntent(intent)
      setIsLeadDialogOpen(true)
    },
    [track]
  )

  const handleSelectPrompt = useCallback(
    (prompt: AnalyticsMcpSample) => {
      setSelectedPromptId(prompt.id)
      track(AnalyticsEvent.RunAnalyticsMcpSample, {
        Question: prompt.question,
        Product: prompt.product,
      })
    },
    [track]
  )

  return (
    <AppLayout>
      <Header unauthCtaLabel="Sign in" unauthCtaHref="/" />
      <Page.Content>
        <Page.Body>
          <StyledContentColumn gap="spacing-12">
            <StyledSection gap="spacing-5">
              <StyledEyebrow>
                <Icon name="sparkle" size="14px" />
                In development · early access by request
              </StyledEyebrow>
              <StyledHeadline>
                Ask a question about your freight and dock data.{' '}
                <StyledHeadlineSoft>Get the answer, not a dashboard.</StyledHeadlineSoft>
              </StyledHeadline>
              <StyledLede>
                Your Loadsmart reporting, reachable from the AI assistant your team already uses.
                Ask in plain language and it answers from the same reports and the same permissions
                you have here — with the rows behind the answer, so you can check it.
              </StyledLede>
              <StyledActions>
                <Button
                  variant="primary"
                  leading={<Icon name="send" />}
                  onClick={() => openLeadForm(HERO_LEAD)}
                >
                  {LEAD_TITLE}
                </Button>
              </StyledActions>
            </StyledSection>

            <StyledSection gap="spacing-3">
              <AskPanel
                prompts={ANALYTICS_MCP_SAMPLES}
                selectedPromptId={selectedPromptId}
                onSelectPrompt={handleSelectPrompt}
                onRequestAccess={() => openLeadForm(SAMPLE_LEAD)}
              />
              <StyledDisclaimer>
                <Icon name="info" size="14px" />
                Illustrative data.
              </StyledDisclaimer>
            </StyledSection>

            <StyledSection gap="spacing-5">
              <Layout.Stack gap="spacing-1">
                <Text variant="heading-lg-bold" color="color-text-primary">
                  What you can ask about
                </Text>
                {/*
                  Deliberately no report count: this page does not fetch the
                  catalog, so a number here would drift the first time someone
                  publishes a report.
                */}
                <Text variant="body-md" color="color-text-tertiary">
                  The same freight and dock reports as your Analytics Library, reachable by asking.
                </Text>
              </Layout.Stack>

              <StyledCapabilityGrid>
                {ANALYTICS_MCP_CAPABILITIES.map(capability => (
                  <StyledCapability key={capability.title}>
                    <Text variant="body-md-bold" color="color-text-primary">
                      {capability.title}
                    </Text>
                    <Text variant="body-sm" color="color-text-secondary">
                      {capability.body}
                    </Text>
                    <StyledCapabilityExample>“{capability.example}”</StyledCapabilityExample>
                  </StyledCapability>
                ))}
              </StyledCapabilityGrid>
            </StyledSection>

            <StyledClosingBand>
              <StyledClosingCopy>
                <StyledClosingTitle>Want this on your own numbers?</StyledClosingTitle>
                <StyledClosingBody>
                  Tell us the question you would ask first. We will get back to you about opening
                  early access on your data.
                </StyledClosingBody>
              </StyledClosingCopy>
              <StyledClosingAction type="button" onClick={() => openLeadForm(CLOSING_LEAD)}>
                <Icon name="send" size="14px" />
                {LEAD_TITLE}
              </StyledClosingAction>
            </StyledClosingBand>
          </StyledContentColumn>
        </Page.Body>
      </Page.Content>

      {leadIntent && (
        <ZendeskDialog
          requestType={RequestType.TryItNow}
          title={LEAD_TITLE}
          description={leadIntent.description}
          open={isLeadDialogOpen}
          onClose={() => setIsLeadDialogOpen(false)}
        />
      )}
    </AppLayout>
  )
}
